// @ts-nocheck
import React from 'react'
import { useDispatch } from 'react-redux'
import * as R from 'ramda'
import { t } from 'ttag'

import { toInterview } from 'ducks/router'
import { showModal } from 'ducks/ux'
import { SwitchAction } from './components'
import { FeatureFlag } from 'components/auth/FeatureFlag'

// utility functions
const anySelected = R.pipe(
  R.propOr([], 'pks'),
  R.length,
  R.lt(0),
)

const oneSelected = R.pipe(
  R.propOr([], 'pks'),
  R.length,
  R.equals(1),
)

const noneSelected = R.complement(anySelected)

// standard action definitions
// name: {
//  reduxAction: [redux action creator function]
//  icon: Icon component to show
//  label: title of action
//  helpText: description of action
//  disabled: action button disabled
//  kebab: action item is contained in a kebab menu
//
// }
const modelActions = verb =>
  ({
    newCandidate: {
      reduxAction: ({ project }) =>
        showModal('EditCandidate', { pk: 'add', project }),
      icon: 'userPlus',
      label: t`Add candidate`,
      helpText: t`Add an candidate to the project.`,
    },
    editCandidate: {
      reduxAction: ({ pks }) => showModal('EditCandidate', { pk: pks[0] }),
      icon: 'edit',
      label: t`Edit`,
      helpText: t`Edit candidate profile.`,
    },
    save: {
      reduxAction: ({ data = {}, model, pks }) =>
        model.patchStart({ data, pk: pks[0] }),
      icon: 'save',
      label: t`Save`,
      helpText: t`Save current changes`,
      disabled: ({ pks = [] }) => !!R.length(pks),
    },
    reset: {
      reduxAction: ({ model, pks }) => model.reset({ pk: pks[0] }),
      icon: 'undo',
      label: t`Reset`,
      helpText: t`Undo all changes since last save`,
    },
    create: {
      reduxAction: ({ model, tab }) => model.toCreate(tab),
      icon: 'plusCircle',
      label: ({ model }) => {
        const word = model.pluralize(1)
        return t`New ${word}`
      },
      helpText: ({ model }) => {
        const word = model.pluralize(1)
        return t`Create new ${word}`
      },
    },
    invite: {
      reduxAction: ({ pks, project }) =>
        showModal('SendInvite', { pks, project }),
      icon: 'mail',
      label: t`Invite`,
      helpText: ({ model, pks = [] }) => {
        const n = pks.length
        const word = model.pluralize(n)
        return n
          ? t`Send invitation mail to ${n} ${word}`
          : t`No ${word} selected for invitation mail.`
      },
      disabled: noneSelected,
    },
    approve: {
      reduxAction: ({ pks }) => showModal('SelectForInterview', { pks }),
      icon: 'userCheck',
      label: t`Move to interview`,
      helpText: t`Select candidates for interview`,
      disabled: noneSelected,
    },
    report: {
      reduxAction: ({ pks = [] }) =>
        showModal('CandidateScores', { pk: pks[0] }),
      icon: 'file',
      label: t`Candidate report`,
      helpText: t`Show the candidate test score report`,
      disabled: R.complement(oneSelected),
    },
    interview: {
      reduxAction: ({ interviewId, featureFlag, pks }) =>
        featureFlag
          ? toInterview({ uuid: interviewId })
          : showModal('PerformInterview', { pk: pks[0] }),
      icon: 'message',
      label: t`Interview`,
      helpText: t`Conduct interview with candidate`,
      disabled: R.complement(oneSelected),
    },
    interviewReport: {
      reduxAction: ({ interviewId }) => toInterview({ uuid: interviewId }),
      icon: 'message',
      label: t`Interview report`,
      helpText: t`Open the candidate's interview report`,
      disabled: R.complement(oneSelected),
    },
    move: {
      reduxAction: ({ pks }) => showModal('MoveCandidates', { pks }),
      icon: 'move',
      label: t`Move`,
      helpText: t`Move candidates to selection step or to a different project`,
      disabled: noneSelected,
      featureFlags: ['moveCandidates'],
    },
    trash: {
      reduxAction: ({ model, pks }) => model.trash({ pks }),
      icon: 'trash',
      label: t`Delete`,
      helpText: ({ model, pks = [] }) => {
        const n = pks.length
        const word = model.pluralize(n)
        return n
          ? t`Delete ${n} selected ${word}`
          : t`No ${word} selected for deletion`
      },
      warning: true,
      disabled: noneSelected,
    },
    import: {
      reduxAction: ({ model, pks }) => showModal('ImportSheet', {}),
      icon: 'importFile',
      label: t`Import`,
      helpText: t`Import candidate list from a spreadsheet file`,
    },
    export: {
      reduxAction: ({ model, pks }) =>
        showModal('ExportSheet', { model: model.endpoint, pks }),
      label: t`Export`,
      icon: 'export',
      helpText: ({ model, pks = [] }) => {
        const n = pks.length
        const word = model.pluralize(n)
        return n
          ? t`Export ${n} selected ${word} to spreadsheet`
          : t`No ${word} selected for export`
      },
      disabled: noneSelected,
    },
  }[verb])

const actionFactory = (
  verb,
  defaultProps = { pks: [] },
  ActionComponent = SwitchAction,
) => ownProps => {
  const dispatch = useDispatch()
  const mergedProps = R.mergeRight(defaultProps, ownProps)
  if (mergedProps.pk && !mergedProps.pks) {
    mergedProps.pks = [mergedProps.pk]
  }
  const factoryProps = R.map(
    R.when(R.is(Function), fn => fn(mergedProps)),
    modelActions(verb),
  )
  const clickHandler = ev => {
    ev.preventDefault()
    dispatch(factoryProps.reduxAction)
  }
  const featureFlags = R.pipe(
    R.propOr([], 'featureFlags'),
    R.map(k => [k, true]),
    R.fromPairs,
  )(factoryProps)

  return (
    <FeatureFlag key={verb} {...featureFlags}>
      <ActionComponent
        onClick={clickHandler}
        {...factoryProps}
        {...mergedProps}
      />
    </FeatureFlag>
  )
}

export default actionFactory
